import { LitElement, html, css, unsafeCSS, nothing } from 'lit';
import styles from './modal.scss?inline';
import { ref, createRef } from 'lit/directives/ref.js';

/**
 * @attribute hide-overlay-desktop - Hides modal overlay up until tablet breakpoint
 */
export default class SotbitModal extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        open: { type: Boolean, reflect: true },
        threshold: { type: Number },
        closeIcon: { type: String, attribute: 'close-icon' },
        hideByOverlay: { type: Boolean, attribute: 'hide-by-overlay' },
        grow: { type: Boolean },
        fullscreen: { type: Boolean, reflect: true },
    };

    constructor() {
        super();

        this.open = false;
        this.threshold = 50;
        this._touchStartY = null;
        this._touchCurrentY = null;
        this._overlayRef = createRef();
        this._titleSlotRef = createRef();
        this._subtitleSlotRef = createRef();
        this._contentSlotRef = createRef();
        this._footerSlotRef = createRef();
        this.isShowHeader = true;
        this.isShowContent = true;
        this.isShowFooter = true;
        this._handleKeyDown = this._handleKeyDown.bind(this);
    }

    firstUpdated() {
        this._setSlotsShowsAndCheckNeedUpdate();
    }

    async willUpdate(changed) {
        if (changed.has('open') && typeof changed.get('open') !== 'undefined') {
            if (this.open) {
                await this.show();
            } else {
                await this.hide();
            }
        }
    }

    async show() {
        if (!this.open) {
            this.open = true;
        }

        await this._overlayRef.value?.show();

        this.dispatchEvent(
            new CustomEvent('open', {
                detail: { value: this.open },
                bubbles: true,
                composed: true,
            })
        );

        this.dispatchEvent(
            new CustomEvent('modal-open', {
                bubbles: true,
                composed: true,
            })
        );
    }

    async hide() {
        if (this.open) {
            this.open = false;
        }

        await this._overlayRef.value?.hide();

        this.dispatchEvent(
            new CustomEvent('close', {
                detail: { value: this.open },
                bubbles: true,
                composed: true,
            })
        );

        this.dispatchEvent(
            new CustomEvent('modal-hide', {
                bubbles: true,
                composed: true,
            })
        );
    }

    connectedCallback() {
        super.connectedCallback();

        window.addEventListener('keydown', this._handleKeyDown);
    }

    disconnectedCallback() {
        this.dispatchEvent(
            new CustomEvent('remove', {
                bubbles: true,
                composed: true,
            })
        );

        super.disconnectedCallback();
        window.removeEventListener('keydown', this._handleKeyDown);
    }

    _handleClose() {
        this._overlayRef.value?.hide();
        this.removeAttribute('open');
    }

    _handleTouchStart({ touches }) {
        this._touchStartY = touches[0].clientY;
    }

    _handleTouchMove({ touches }) {
        this._touchCurrentY = touches[0].clientY;
    }

    _handleTouchEnd() {
        const diff = this._touchCurrentY - this._touchStartY;

        if (diff > this.threshold) {
            this.removeAttribute('open');
        }
    }

    _setSlotsShowsAndCheckNeedUpdate() {
        const slotsRefs = [
            {
                ref: this._titleSlotRef,
                isShow: this.isShowHeader,
                name: 'header',
            },
            {
                ref: this._contentSlotRef,
                isShow: this.isShowContent,
                name: 'content',
            },
            {
                ref: this._footerSlotRef,
                isShow: this.isShowFooter,
                name: 'footer',
            },
        ];

        let needUpdate = false;
        for (const i in slotsRefs) {
            const { ref, isShow, name } = slotsRefs[i];
            const elements = ref?.value?.assignedElements();

            if (elements?.length > 0 !== isShow) {
                needUpdate = true;

                const hasSubheader = this._subtitleSlotRef?.value?.assignedElements() > 0;
                switch (name) {
                    case 'header':
                        this.isShowHeader = !isShow || this.closeIcon || hasSubheader;
                        break;
                    case 'content':
                        this.isShowContent = !isShow;
                        break;
                    case 'footer':
                        this.isShowFooter = !isShow;
                        break;
                }
            }
        }

        return needUpdate;
    }

    _handleSlotChange() {
        if (this._setSlotsShowsAndCheckNeedUpdate()) {
            this.requestUpdate();
        }
    }

    _handleKeyDown(event) {
        if (!this.closeIcon || !this.hideByOverlay) {
            return;
        }

        if (this.open && event.key === 'Escape') {
            this._handleClose();
        }
    }

    render() {
        return html` <sotbit-overlay
                ${ref(this._overlayRef)}
                @click="${this.hideByOverlay ? this._handleClose : nothing}"
            ></sotbit-overlay>
            <div @slotchange="${this._handleSlotChange}" class="wrapper" part="modal">
                <div
                    @touchstart="${this._handleTouchStart}"
                    @touchmove="${this._handleTouchMove}"
                    @touchend="${this._handleTouchEnd}"
                    class="mobile-close"
                ></div>
                <div
                    class="header ${!this.isShowHeader ? 'hidden' : ''}"
                    part="header ${!this.isShowHeader ? 'header-hidden' : ''}"
                >
                    <div class="header__inner" part="header-inner">
                        <div class="title" part="title">
                            <slot name="title" ${ref(this._titleSlotRef)}></slot>
                        </div>
                        ${this.closeIcon && !this.fullscreen
                            ? html`
                                  <sotbit-icon
                                      class="icon-close"
                                      @click="${this._handleClose}"
                                      icon="${this.closeIcon}"
                                      pointer
                                      variant="secondary"
                                  ></sotbit-icon>
                              `
                            : ''}
                    </div>
                    <slot name="subtitle" ${ref(this._subtitleSlotRef)}></slot>
                </div>
                <div class="content ${!this.isShowContent ? 'hidden' : ''}" part="content">
                    <sotbit-scroll-panel class="scroll-panel" part="scroll-panel">
                        <div class="content-wrapper" part="content-wrapper">
                            <slot name="content" ${ref(this._contentSlotRef)}></slot>
                        </div>
                    </sotbit-scroll-panel>
                </div>

                <div
                    class="footer ${!this.isShowFooter ? 'hidden' : ''}"
                    part="footer${!this.isShowFooter ? ' footer-hidden' : ''}"
                >
                    <slot ${ref(this._footerSlotRef)} name="footer"></slot>
                </div>
            </div>`;
    }
}
